import React, { useEffect, useMemo, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { TableSchema } from '../../../../../components/DataTable/types'
import { format, fromUnixTime } from 'date-fns'
import {
  AdCreative,
  AdGroup,
  Campaign,
  CampaignAd,
  CampaignAdset,
} from '@marketing-milk/interfaces'
import { DataTable } from 'components/DataTable'
import { Button } from '@material-ui/core'
import { IconButton, Tooltip } from '@mui/material'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import DialogModal from 'components/material/dialog/Dialog'
import { StatusIndicator } from '../status-indicator'
import { OnPauseOrResumeAdObjectHandler } from '../../../../../app/services/campaign-builder/campaign/Campaign.types'
import { PauseAdObject } from '../PauseAdObject'
import { campaignService } from '../../../../../app/services'
import {
  adCreativeService,
  FormatService,
  Loading,
  useGetBusinessID,
} from '@marketing-milk/frontend'
import { useFetchCreativePreview } from '../../../../../hooks/creatives/fetch-creative-preview'

// The "View on Facebook" button below the preview, plus the gap above it. The preview is
// scaled to leave this much room so the button stays in the window with the ad.
const DESTINATION_BUTTON_HEIGHT = 60

type Props = {
  campaign: Campaign
  adSet: CampaignAdset
  campaignHasEnded: boolean
  campaignIsFuture: boolean
  onPauseOrResume: OnPauseOrResumeAdObjectHandler
}

export const AdsTable = ({
  campaign,
  adSet,
  campaignHasEnded,
  campaignIsFuture,
  onPauseOrResume,
}: Props) => {
  const businessID = useGetBusinessID()
  const [selectedAd, setSelectedAd] = useState<CampaignAd>()
  // The thin `CampaignAd` lacks the copy/media fields the local fallback needs, so
  // fetch the full creative when the preview dialog opens.
  const [previewCreative, setPreviewCreative] = useState<AdCreative>()
  const [previewCreativeFailed, setPreviewCreativeFailed] = useState(false)
  const [adGroups, setAdGroups] = useState<AdGroup[]>()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const getAdsets = async () => {
      setLoading(true)
      setAdGroups(await campaignService.getAdsForAdset(Number(businessID), campaign.id, adSet.id))
      setLoading(false)
    }

    getAdsets()
  }, [businessID, campaign, adSet])

  const { retrievePreview, adCreativePreview, loadingPreview, refreshing } =
    useFetchCreativePreview(selectedAd?.creativeID, undefined, { suppressErrorToast: true })

  const previewWidth = useMemo(() => {
    const width = Number(ReactHtmlParser(adCreativePreview ?? '')[0]?.props?.width)
    // Meta sometimes returns thumbnail-sized frame attributes (260 × 135) even though the
    // embedded mobile feed ad needs a full-size viewport, so the width is clamped: the dialog
    // stays the shape of a phone-sized ad rather than taking the frame's word for it.
    return Number.isFinite(width) ? Math.min(420, Math.max(375, width)) : 375
  }, [adCreativePreview])

  useEffect(() => {
    retrievePreview(selectedAd?.creativeID)
  }, [selectedAd?.creativeID])

  // Fetch the full creative for the local fallback whenever the selected ad changes.
  useEffect(() => {
    const creativeID = selectedAd?.creativeID
    setPreviewCreative(undefined)
    setPreviewCreativeFailed(false)
    if (!creativeID) return
    let active = true
    adCreativeService
      .getByID(Number(businessID), creativeID)
      .then(creative => {
        if (active) setPreviewCreative(creative)
      })
      .catch(() => {
        if (active) setPreviewCreativeFailed(true)
      })
    return () => {
      active = false
    }
  }, [businessID, selectedAd?.creativeID])

  type AdTableColumnNames =
    | 'Off / On'
    | 'Ad Name'
    | 'Primary Text'
    | 'Preview'
    | 'Revenue'
    | 'Spend'
    | 'Cost Per'
    | 'RoAS'
    | 'KPI'
    | 'Delivery Status'
    | 'Start Date'
    | 'End Date'
  const adTableSchema: TableSchema<CampaignAd, AdTableColumnNames> = {
    'Off / On': {
      tooltipText:
        'This column indicates whether your adset is currently on or off. When switched on, it will be able to run based on its schedule and settings. When switched off, it will not run.',
      render: ad => (
        <PauseAdObject
          checkedDefault={ad.activeStatus === 'ACTIVE'}
          disabled={!ad.activeStatus}
          onPauseOrResume={checked => onPauseOrResume('Ad', ad.id, checked ? 'resume' : 'pause')}
        />
      ),
    },
    'Ad Name': {
      render: ad => ad.name || <p style={{ color: 'darkred' }}>N/A</p>,
    },
    'Start Date': {
      render: ad => (ad.startDate ? format(fromUnixTime(ad.startDate), 'P') : 'N/A'),
    },
    'End Date': {
      render: ad => (ad.endDate ? format(fromUnixTime(ad.endDate), 'P') : 'N/A'),
    },
    'Delivery Status': {
      render: data => (
        <StatusIndicator
          status={data.status}
          errors={data.errors}
          hasEnded={campaignHasEnded}
          isFuture={campaignIsFuture}
        />
      ),
    },
    Revenue: {
      render: ({ revenue }) =>
        revenue ? FormatService.getValue(revenue, { type: 'currency' }) : 'N/A',
    },
    Spend: {
      render: ({ spend }) => (spend ? FormatService.getValue(spend, { type: 'currency' }) : 'N/A'),
    },
    'Cost Per': {
      render: ({ costPerCurrent }) =>
        costPerCurrent ? FormatService.getValue(costPerCurrent, { type: 'currency' }) : 'N/A',
    },
    RoAS: {
      render: ({ roas }) => (roas ? `${FormatService.getValue(roas)}x` : 'N/A'),
    },
    KPI: {
      tooltipText:
        'Your ads may not total up to be the same as the adset-level. This is due to how ad results are currently extrapolated.',
      render: ({ kpi }) => FormatService.getValue(kpi),
    },
    Preview: {
      tooltipText: 'See the ad as it appears on Facebook',
      render: ad => (
        <Tooltip title="Preview ad">
          <IconButton
            size="small"
            color="primary"
            aria-label={`Preview ${ad.name || 'this ad'}`}
            onClick={() => setSelectedAd(ad)}
          >
            <VisibilityOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  }

  return (
    <>
      <DialogModal
        maxWidth="xs"
        dense
        onOpen={() => {}}
        isOpen={!!selectedAd}
        onClose={() => setSelectedAd(undefined)}
        content={
          <div>
            {/* The creative is fetched when the dialog opens, so without these the dialog is an
                empty white box while it loads and stays empty if the ad has since been deleted. */}
            {previewCreative ? (
              <CreativePreview
                creative={previewCreative}
                iframeSource={getIFrameSource(adCreativePreview ?? '')}
                fitToWindow={{
                  width: previewWidth,
                  reservedHeight: selectedAd?.destinationURL ? DESTINATION_BUTTON_HEIGHT : 0,
                }}
                loading={loadingPreview}
                refreshing={refreshing}
                onRetry={() => retrievePreview(selectedAd?.creativeID)}
              />
            ) : previewCreativeFailed ? (
              <div role="alert" className="px-6 py-12 text-center">
                <p className="font-semibold text-mm-neutral-1-900">
                  This ad&rsquo;s creative could not be loaded
                </p>
                <p className="mt-1 text-sm text-mm-neutral-1-600">
                  {selectedAd?.name
                    ? `“${selectedAd.name}” may have been deleted on Facebook.`
                    : ''}
                </p>
              </div>
            ) : (
              selectedAd && (
                <div className="px-6 py-12">
                  <Loading />
                </div>
              )
            )}
            {selectedAd && selectedAd.destinationURL && (
              <div className="pt-3">
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    window.location.href = selectedAd.destinationURL ?? 'https://facebook.com'
                  }}
                >
                  View on <i className="ml-2 fab fa-facebook-square text-white" />
                </Button>
              </div>
            )}
          </div>
        }
      />
      <div>
        {!loading && adGroups?.length ? (
          // Cascading campaigns split an adset's ads into groups. The groups carry no name of
          // their own, so they are separated by a rule and the column headings are printed once
          // rather than repeating per group and reading as a fresh table each time.
          adGroups.map((adGroup, i) => (
            <div
              key={`adset-${adSet.id}-${i}`}
              className={i > 0 ? 'border-mm-neutral-2-200 mt-2 border-t pt-2' : undefined}
            >
              <DataTable<any>
                tableSchema={adTableSchema}
                recordsList={adGroup}
                hideColumnFilter
                hidePagination
                hideSearch
                hideHeader={i > 0}
              />
            </div>
          ))
        ) : loading ? (
          <Loading />
        ) : (
          <p className="text-mm-neutral-2-500 py-4 text-center text-sm">
            This adset has no ads connected to it.
          </p>
        )}
      </div>
    </>
  )
}
